import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { createContext, forwardRef, useMemo } from 'react';
import { Icon16Check } from '@figma/fpl-icons/icon-16-check';
import { Icon16Mixed } from '@figma/fpl-icons/icon-16-mixed';
import { useIdProvider } from '../hooks/id-provider.js';
import { useInteractionHandler } from '../hooks/use-interaction-handler.js';
import { useMergeRefs } from '../hooks/use-merge-refs.js';
import { useValidatedContext } from '../hooks/use-validated-context.js';
import { accept } from '../utils/event.js';
import { isStandardShortcutKeyPressed } from '../utils/modifiers.js';
import { Group } from './menu-v2.group.js';
import { MenuInternalContext, useMenuItemSelection, useMenuItemSurfaceProps } from './menu-v2.hooks.js';
import { ItemDisplay } from './menu-v2.item.js';
import { item, checkboxGroupItemCheckmark } from './menu-v2.css.js';

const GroupContext = createContext(null);
/**
 * A checkbox group is used when multiple options are able to be selected in a menu
 *
 * @compose { "parent": ["MenuV2.Root", "MenuV2.SubMenu"], "multi": true, "strictChildren": true }
 * @see http://fpl.figma.design/primitives/menu/
 **/
const CheckboxGroup = forwardRef(({ onChange: onChangeIn, disabled = false, children, recordingKey, selectedItems = [], ...props }, ref) => {
    const [id, IdProvider] = useIdProvider();
    const recordedAction = useInteractionHandler(onChangeIn, { eventName: 'change', recordingKey }, [onChangeIn]);
    const ctx = useMemo(() => {
        const onChange = (value, checked, event) => {
            const safeEvent = event instanceof Event ? event : undefined;
            if (checked) {
                recordedAction(selectedItems.concat([value]), { event: safeEvent });
            }
            else {
                recordedAction(selectedItems.filter((x) => x !== value), { event: safeEvent });
            }
        };
        return { onChange, selectedItems, disabledGroup: disabled };
    }, [selectedItems, disabled, recordedAction]);
    return (jsx(IdProvider, { value: id, children: jsx(GroupContext.Provider, { value: ctx, children: jsx(Group, { ref: ref, ...props, children: children }) }) }));
});
CheckboxGroup.displayName = 'MenuV2.CheckboxGroup';
/**
 * Menu.CheckboxGroupItem
 *
 * @compose { "parent": ["MenuV2.CheckboxGroup"], "multi": true }
 */
const CheckboxGroupItem = forwardRef(({ disabled, value, children, lead, targetId, trail, ...props }, refIn) => {
    const { onCloseRoot, isClickHold } = useValidatedContext(MenuInternalContext, 'MenuV2.CheckboxGroupItem', 'MenuV2.CheckboxGroup');
    const { selectedItems, onChange, disabledGroup } = useValidatedContext(GroupContext, 'MenuV2.CheckboxGroupItem', 'MenuV2.CheckboxGroup');
    const isSelected = useMemo(() => selectedItems.includes(value), [value, selectedItems]);
    const isDisabled = disabledGroup || disabled;
    const { itemProps, targetRef } = useMenuItemSelection({ targetId });
    const ref = useMergeRefs(refIn, targetRef);
    const { interactionProps, paintProps } = useMenuItemSurfaceProps({
        targetId,
        disabled: isDisabled,
        treatment: 'brand',
    });
    const handleClick = (e) => {
        if (!isDisabled) {
            onChange(value, !isSelected, e);
        }
        accept(e);
    };
    const handlePointerUp = (e) => {
        if (isClickHold.current) {
            handleClick(e);
            onCloseRoot(); // close on click-and-drag
        }
    };
    const handleKeyDown = (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
            if (!isDisabled)
                onChange(value, !isSelected, e);
            accept(e); // accept event regardless of disabled
        }
    };
    return (jsx("li", { ...interactionProps, ...props, ...itemProps, className: item, ref: ref, "aria-checked": isSelected, "aria-disabled": isDisabled, role: "menuitemcheckbox", onClick: handleClick, onPointerUp: handlePointerUp, onKeyDown: handleKeyDown, children: jsx(ItemDisplay, { ...paintProps, lead: lead, trail: jsxs(Fragment, { children: [trail, jsx("span", { className: checkboxGroupItemCheckmark, children: isSelected && jsx(Icon16Check, { "aria-hidden": true }) })] }), variant: "default", children: children }) }));
});
CheckboxGroupItem.displayName = 'MenuV2.CheckboxGroupItem';
/**
 * Menu.CheckboxItem
 *
 * @compose { "parent": ["MenuV2.Root", "MenuV2.Group", "MenuV2.SubMenu"], "multi": true }
 */
const CheckboxItem = forwardRef(({ checked = false, children, disabled, lead, mixed, targetId, trail, recordingKey, closeOnClick = true, onChange, ...props }, refIn) => {
    const { onCloseRoot, isClickHold } = useValidatedContext(MenuInternalContext, 'MenuV2.CheckboxItem', 'MenuV2.Root');
    const recordedAction = useInteractionHandler(onChange, {
        eventName: 'change',
        recordingKey,
    }, [onChange]);
    const { itemProps, targetRef } = useMenuItemSelection({ targetId });
    const ref = useMergeRefs(refIn, targetRef);
    const { interactionProps, paintProps } = useMenuItemSurfaceProps({
        targetId,
        disabled,
        treatment: 'brand',
    });
    const handleClick = (e) => {
        if (!disabled) {
            const newValue = mixed ? true : !checked;
            recordedAction(newValue, { event: e });
            if (closeOnClick && (!isStandardShortcutKeyPressed(e) || isClickHold.current)) {
                onCloseRoot();
            }
        }
        accept(e);
    };
    const handleKeyDown = (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
            if (!disabled) {
                const newValue = mixed ? true : !checked;
                recordedAction(newValue, { event: e });
                if (closeOnClick)
                    onCloseRoot();
            }
            accept(e); // accept event regardless of disabled status
        }
    };
    return (jsx("li", { ...interactionProps, ...props, ...itemProps, className: item, ref: ref, "aria-checked": checked, "aria-disabled": disabled, role: "menuitemcheckbox", onClick: handleClick, onPointerUp: (e) => {
            if (isClickHold.current)
                handleClick(e);
        }, onKeyDown: handleKeyDown, children: jsx(ItemDisplay, { ...paintProps, selectIcon: mixed ? jsx(Icon16Mixed, { "aria-hidden": true }) : jsx(Icon16Check, {}), lead: lead, trail: trail, variant: "default", children: children }) }));
});
CheckboxItem.displayName = 'MenuV2.CheckboxItem';

export { CheckboxGroup, CheckboxGroupItem, CheckboxItem };
//# sourceMappingURL=menu-v2.checkbox.js.map
